import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { apiAdminMessages, apiAdminExpertRequests, apiMarkMessageRead, apiAdminStripeCleanup } from "../lib/api";
import { Inbox, ShieldAlert, Loader2, Mail, MailOpen, CheckCheck, CreditCard } from "lucide-react";

const riskColor = (r) => {
  switch ((r || "").toUpperCase()) {
    case "DANGER": return "text-rose-400 bg-rose-500/10 border-rose-500/30";
    case "CAUTION": return "text-amber-400 bg-amber-50 border-amber-200";
    case "SAFE": return "text-emerald-400 bg-emerald-50 border-emerald-200";
    default: return "text-slate-500 bg-slate-50 border-slate-200";
  }
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("nl-BE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AdminInbox() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expert, setExpert] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupMsg, setCleanupMsg] = useState("");

  const isAdmin = user && user.role === "ADMIN";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, e] = await Promise.all([apiAdminMessages(), apiAdminExpertRequests()]);
      setMessages(m.items || []); setUnread(m.unread || 0); setExpert(e.items || []);
    } catch (_) { /* handled by guard below */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user && !isAdmin) return;
    if (isAdmin) load();
  }, [user, isAdmin, load, navigate]);

  const markRead = async (id) => {
    try {
      await apiMarkMessageRead(id);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* ignore */ }
  };

  const runStripeCleanup = async () => {
    setCleanupBusy(true); setCleanupMsg("");
    try {
      const res = await apiAdminStripeCleanup();
      setCleanupMsg(t("admin.stripeCleanupDone").replace("{checked}", res.checked ?? 0).replace("{cleared}", res.cleared ?? 0));
    } catch (_) {
      setCleanupMsg(t("admin.stripeCleanupError"));
    } finally { setCleanupBusy(false); }
  };

  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;
  }
  if (user && !isAdmin) {
    return (
      <div className="tw-grid-bg flex min-h-screen items-center justify-center px-4 pt-28 pb-16" data-testid="admin-denied">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-rose-400" />
          <p className="mt-3 text-slate-600">{t("admin.denied")}</p>
        </div>
      </div>
    );
  }

  const tabBtn = (id, label, count) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
        tab === id ? "bg-[#1E3A8A] text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`rounded-full px-2 py-0.5 text-xs ${tab === id ? "bg-[#F8FAFC]/20 text-white" : "bg-[#1E3A8A]/20 text-blue-700"}`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="tw-grid-bg min-h-screen px-4 pt-28 pb-16 sm:px-6 lg:px-8" data-testid="admin-inbox-page">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#0D9488]">
            <Inbox className="h-6 w-6 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="admin-title">{t("admin.title")}</h1>
            <p className="text-sm text-slate-500">{t("admin.subtitle")}</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {tabBtn("messages", t("admin.tabMessages"), unread)}
          {tabBtn("expert", t("admin.tabExpert"), expert.length)}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-stripe-cleanup">
          <div className="flex items-start gap-3">
            <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{t("admin.stripeCleanupTitle")}</p>
              <p className="text-xs text-slate-500">{t("admin.stripeCleanupDesc")}</p>
              {cleanupMsg && <p className="mt-1 text-xs font-medium text-emerald-700" data-testid="admin-stripe-cleanup-result">{cleanupMsg}</p>}
            </div>
          </div>
          <button data-testid="admin-stripe-cleanup-btn" onClick={runStripeCleanup} disabled={cleanupBusy}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60">
            {cleanupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {t("admin.stripeCleanupBtn")}
          </button>
        </div>

        {loading ? (
          <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
        ) : tab === "messages" ? (
          <div className="mt-6 space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 && <p className="text-slate-500">{t("admin.emptyMessages")}</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid="admin-message-card"
                className={`rounded-2xl border p-5 transition-colors ${m.read ? "border-slate-200 bg-white" : "border-blue-200 bg-blue-50"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {m.read ? <MailOpen className="h-4 w-4 text-slate-500" /> : <Mail className="h-4 w-4 text-blue-700" />}
                      <span className="truncate font-semibold text-slate-900">{m.subject}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {t("admin.from")}: <span className="text-slate-700">{m.name}</span> · <a href={`mailto:${m.email}`} className="text-blue-700 hover:text-blue-700">{m.email}</a>
                    </p>
                    <p className="text-xs text-slate-500">{t("admin.category")}: {m.category} · {fmtDate(m.created_at)}</p>
                  </div>
                  {!m.read && (
                    <button data-testid="admin-mark-read" onClick={() => markRead(m.id)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                      <CheckCheck className="h-3.5 w-3.5" /> {t("admin.markRead")}
                    </button>
                  )}
                </div>
                <p className="mt-3 whitespace-pre-wrap border-t border-slate-200 pt-3 text-sm text-slate-600">{m.message}</p>
                <p className="mt-2 text-xs text-slate-500">{m.emailed ? t("admin.emailedYes") : t("admin.emailedNo")}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="admin-expert-list">
            {expert.length === 0 && <p className="text-slate-500">{t("admin.emptyExpert")}</p>}
            {expert.map((r) => (
              <div key={r.id} data-testid="admin-expert-card" className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${riskColor(r.risk_level)}`}>
                    {r.risk_level || "—"}{typeof r.risk_score === "number" ? ` · ${r.risk_score}` : ""}
                  </span>
                  <span className="text-sm text-slate-600">{t("admin.reqType")}: {r.input_type || "—"}</span>
                </div>
                <p className="mt-2 text-sm text-slate-500">{t("admin.reqBy")}: {r.requester_type} · {t("admin.reqStatus")}: {r.status}</p>
                <p className="mt-1 text-xs text-slate-500">{fmtDate(r.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
